import DOMPurify from 'dompurify'
import md from '@base/markdown'

// Define some maps for caching sanitized inputs
const sanitizationCache: Map<string, string> = new Map()
const markdownCache: Map<string, string> = new Map()

/**
 * Sanitizes the input if not already cached, otherwise returns the cached version.
 * @param input The raw user-generated string that needs to be sanitized.
 * @returns The sanitized string.
 */
export const sanitize = (input: string): string => {
  // Check if input is already sanitized and cached
  if (sanitizationCache.has(input)) {
    return sanitizationCache.get(input) as string
  }

  // Sanitize the input using DOMPurify
  const sanitizedInput = DOMPurify.sanitize(input)

  // Cache the sanitized version
  sanitizationCache.set(input, sanitizedInput)

  return sanitizedInput
}

/**
 * Clears the cache for a specific sanitized input.
 * @param input The string whose cache entry should be invalidated.
 */
export const invalidateSanitizationCache = (input: string): void => {
  sanitizationCache.delete(input)
}

/**
 * Clears the entire sanitization cache.
 */
export const clearSanitizationCache = (): void => {
  sanitizationCache.clear()
}

/**
 * Converts markdown to HTML, sanitizes the resulting HTML, and caches the result.
 * @param markdown The raw markdown string to be converted and sanitized.
 * @returns The sanitized HTML string, either from cache or freshly converted.
 */
export const markupAndSanitize = (markdown: string): string => {
  // Check if the markdown content is already in the cache
  if (markdownCache.has(markdown)) {
    return markdownCache.get(markdown) as string
  }

  // Convert markdown to HTML
  const htmlContent = md.render(markdown)

  // Sanitize the resulting HTML
  const sanitizedHtml = DOMPurify.sanitize(htmlContent)

  // Cache the sanitized HTML
  markdownCache.set(markdown, sanitizedHtml)

  return sanitizedHtml
}

/**
 * Clears the cache for a specific markdown string.
 * @param markdown The markdown string whose cache entry should be invalidated.
 * @example Invalidating cache while editing
 *
 *
 * import { useState } from 'react'
 * import { markupAndSanitize, invalidateMarkdownCache } from '../utils/sanitize'
 *
 * const EditableMarkdown: React.FC<MarkerProps> = ({ description }) => {
 *   const [text, setText] = useState(description)
 *
 *   const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
 *     const newText = event.target.value
 *
 *     // Invalidate the old cached version before updating
 *     invalidateMarkdownCache(text)
 *
 *     // Update the state with the new markdown content
 *     setText(newText)
 *   }
 *
 *   const sanitizedHtml = markupAndSanitize(text)
 *
 *   return (
 *     <div>
 *       <textarea value={text} onChange={handleTextChange} />
 *       <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
 *     </div>
 *   )
 * }
 */
export const invalidateMarkdownCache = (markdown: string): void => {
  markdownCache.delete(markdown)
}

/**
 * Clears the entire markdown cache.
 */
export const clearMarkdownCache = (): void => {
  markdownCache.clear()
}
